import { Box, type SvgIconTypeMap, type SxProps, type Theme } from '@mui/material'
import type { OverridableComponent } from '@mui/material/OverridableComponent'

// biome-ignore lint/complexity/noBannedTypes: match type from MUI
type IconType = OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
  muiName: string
}

type IconPosition =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'middleLeft'
  | 'middleCenter'
  | 'middleRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight'

type PositionedIcon = {
  icon: IconType
  position: IconPosition
  size?: string | number
  sx?: SxProps<Theme>
}

type Props = {
  centerIcon: IconType
  centerIconSize?: string | number
  centerIconSx?: SxProps<Theme>
  overlayIcons?: PositionedIcon[]
  offset?: number
}

const getPositionStyles = (position: IconPosition, offset: number): SxProps<Theme> => {
  const baseStyles: SxProps<Theme> = {
    position: 'absolute',
    backgroundColor: 'background.paper',
    borderRadius: '50%',
    padding: '1px',
  }

  switch (position) {
    case 'topLeft':
      return { ...baseStyles, top: -offset, left: -offset }
    case 'topCenter':
      return { ...baseStyles, top: -offset, left: '50%', transform: 'translateX(-50%)' }
    case 'topRight':
      return { ...baseStyles, top: -offset, right: -offset }
    case 'middleLeft':
      return { ...baseStyles, top: '50%', left: -offset, transform: 'translateY(-50%)' }
    case 'middleCenter':
      return { ...baseStyles, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    case 'middleRight':
      return { ...baseStyles, top: '50%', right: -offset, transform: 'translateY(-50%)' }
    case 'bottomLeft':
      return { ...baseStyles, bottom: -offset, left: -offset }
    case 'bottomCenter':
      return { ...baseStyles, bottom: -offset, left: '50%', transform: 'translateX(-50%)' }
    case 'bottomRight':
      return { ...baseStyles, bottom: -offset, right: -offset }
  }
}

export default function IconStack({
  centerIcon: CenterIcon,
  centerIconSize = '1.5rem',
  centerIconSx = {},
  overlayIcons = [],
  offset = 2,
}: Props) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CenterIcon sx={{ fontSize: centerIconSize, ...centerIconSx }} />
      {overlayIcons.map(({ icon: OverlayIcon, position, size = '0.875rem', sx: customSx = {} }) => {
        const uniqueKey = `${position}-${OverlayIcon.muiName}-${size}`
        return (
          <OverlayIcon
            key={uniqueKey}
            sx={
              {
                ...getPositionStyles(position, offset),
                fontSize: size,
                ...customSx,
              } as SxProps<Theme>
            }
          />
        )
      })}
    </Box>
  )
}
